import { type SortingState } from '@tanstack/react-table'
import { type Dispatch, type SetStateAction, useEffect } from 'react'

import {
  type NAVv2ArrayValueFilters,
  type NAVv2Filters,
  type NAVv2SingleValueFilters,
} from 'components/hooks/useGetBrokersMissingPayments'

import { filterConfigurations } from './NeedsAttentionFilters'
import { type NeedsAttentionPagination } from './NeedsAttentionTable'

type NeedsAttentionSyncFiltersWithUrlProps = {
  filters: NAVv2Filters
  pagination: NeedsAttentionPagination
  sorting: SortingState
  setV2Filters: Dispatch<SetStateAction<NAVv2Filters>>
  isNAVv2ToolingEnabled: boolean
}

const sortableKeys = ['created_at', 'state']

const NeedsAttentionSyncFiltersWithURL = ({
  filters,
  pagination,
  sorting,
  setV2Filters,
  isNAVv2ToolingEnabled,
}: NeedsAttentionSyncFiltersWithUrlProps) => {
  // Set all filters from the URL on page load.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    // Track if any filters have been explicitly set in the URL. We cannot rely on state for this because state updates
    // are asynchronous and batched together.
    let hasFilters = false

    Object.values(filterConfigurations).forEach(({ key, isMultiValue }) => {
      if (isMultiValue) {
        const includeValues = params.get(`filter[${key}]`)
        const excludeValues = params.get(`filter[excludes_${key}]`)
        if (
          (includeValues && includeValues.length > 0) ||
          (excludeValues && excludeValues.length > 0)
        ) {
          hasFilters = true
          setV2Filters((prev) => ({
            ...prev,
            [key]: {
              ...(includeValues
                ? { include: includeValues.split(',') as string[] }
                : {}),
              ...(excludeValues
                ? { exclude: excludeValues.split(',') as string[] }
                : {}),
            },
          }))
        }
      } else {
        const value = params.get(`filter[${key}]`)
        if (value) {
          hasFilters = true
          setV2Filters((prev) => ({
            ...prev,
            [key]: value || '',
          }))
        }
      }
    })

    // If no filters are set, default to active and silenced statuses.
    if (!hasFilters && isNAVv2ToolingEnabled) {
      setV2Filters((prev) => ({
        ...prev,
        statuses: { include: ['active', 'silenced'] },
        reimbursement_only: 'false',
      }))
    }
  }, [])

  // update the URL as sorting or column filters change
  useEffect(() => {
    const pageURL = new URL(window.location.href)

    sortableKeys.forEach((sortKey) => {
      pageURL.searchParams.delete(`sort[${sortKey}]`)
    })

    const pageIndex = pagination.pageIndex.toString()
    pageURL.searchParams.set('page', pageIndex)

    Object.values(filterConfigurations).forEach(({ key, isMultiValue }) => {
      if (isMultiValue) {
        const filterKey = key as keyof NAVv2ArrayValueFilters
        const values = filters[filterKey] || []

        Object.entries({ include: [], exclude: [], ...values }).forEach(
          ([filterType, value]) => {
            const searchParamsKey = `filter[${
              filterType === 'exclude' ? 'excludes_' : ''
            }${filterKey}]`

            if (value?.length > 0) {
              pageURL.searchParams.set(
                searchParamsKey,
                value.join(',') as string
              )
            } else {
              pageURL.searchParams.delete(searchParamsKey)
            }
          }
        )
      } else {
        const filterKey = key as keyof NAVv2SingleValueFilters
        const value = filters[filterKey]
        if (value) {
          pageURL.searchParams.set(`filter[${filterKey}]`, value as string)
        } else {
          pageURL.searchParams.delete(`filter[${filterKey}]`)
        }
      }
    })

    if (sorting.length > 0) {
      const { id, desc } = sorting[0]
      // Tanstack is expecting the column header ID to be 'status'
      const sortKey = id === 'status' ? 'state' : id
      pageURL.searchParams.set(`sort[${sortKey}]`, desc ? 'desc' : 'asc')
    }

    // change the URL of the page without reloading the page
    // Ensure the first state update replaces the current history entry
    if (!window.history.state) {
      window.history.replaceState({}, '', pageURL.toString())
    } else {
      window.history.pushState({}, '', pageURL.toString())
    }
  }, [pagination.pageIndex, sorting, JSON.stringify(filters)])

  // Keep state in sync when the user navigates browser history (back/forward).
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search)

      Object.values(filterConfigurations).forEach(({ key, isMultiValue }) => {
        if (isMultiValue) {
          const includesValues = params.get(`filter[${key}]`)
          const excludesValues = params.get(`filter[excludes_${key}]`)

          if (includesValues || excludesValues) {
            setV2Filters((prev) => ({
              ...prev,
              [key]: {
                ...(includesValues
                  ? { include: includesValues.split(',') as string[] }
                  : {}),
                ...(excludesValues
                  ? { exclude: excludesValues.split(',') as string[] }
                  : {}),
              },
            }))
          }
        } else {
          const value = params.get(`filter[${key}]`)
          if (value) {
            setV2Filters((prev) => ({
              ...prev,
              [key]: value || '',
            }))
          }
        }
      })
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  return null
}

export default NeedsAttentionSyncFiltersWithURL
